import React from 'react';
import { FormattedMessage } from 'react-intl';
import { FieldValues } from 'react-hook-form';
import { GIButton, Modal } from 'app-components';
import { useDispatch, useSelector } from 'react-redux';

import { useApiHandler } from '../hooks/useApiHandler';
import { useFormHandler } from '../hooks/useFormHandler';
import { ROOT_STATE } from '@store/store.types';
import { refreshTicketList, statusChanged } from '../constants/ticketlist.utils';
import { ICancelReasonModal } from '../constants/ticketlist.interface';
import { TICKET_LIST_ACTION_TYPES } from '@constants/actionTypes/tikcetListActionTypes';
import { StyledCharacterReamining, StyledInputContainer, StyledModal } from '../constants/ticketlist.styled';
import { CANCEL_REASON_COLS, CANCEL_REASON_MAX_LENGTH, CANCEL_REASON_ROWS } from '../constants/ticketlist.constants';
import { API_STATUS_CODES } from '@constants/common.constants';

export const CancelTicketReasonModal = ({ onClose, isAdvanceSearch }: ICancelReasonModal) => {
    const dispatch = useDispatch();
    const {
        modalData: { ticketId },
        tickets: { ticketListJson },
        controller: { isSimplifiedView, isArchivedAllowed, isAssignAllowed },
        listApiType,
    } = useSelector((state: ROOT_STATE) => state.ticketMgmt.ticketsList);

    const { filteredListAPI, listAPI, defaultListAPI, cancelTicketAPI } = useApiHandler();
    const { cancelTicketForm } = useFormHandler();
    const { handleSubmit, watch, register } = cancelTicketForm;

    const onSubmit = async (formData: FieldValues) => {
        const { comment } = formData;

        const payload = {
            comment: comment,
            commentLength: comment?.length,
            ticketId: ticketId,
            csrftoken: window.csrf_token,
        };
        const { statusCode } = await cancelTicketAPI(dispatch, payload);
        if (statusCode === API_STATUS_CODES.OK) {
            const index = ticketListJson.findIndex(ticket => ticket.encryptedId === ticketId);
            if (index !== -1) {
                const updatedTickets = [...ticketListJson];
                updatedTickets[index] = {
                    ...updatedTickets[index],
                    status: 'Canceled',
                };
                updatedTickets[index] = statusChanged(
                    updatedTickets[index],
                    isSimplifiedView,
                    isArchivedAllowed,
                    isAssignAllowed,
                );
                dispatch({ type: TICKET_LIST_ACTION_TYPES.UPDATE_TICKETS, payload: updatedTickets });
            }
            refreshTicketList({
                dispatch,
                listApiType,
                defaultListAPI,
                listAPI,
                filteredListAPI,
            });
        }
        onClose();
    };

    return (
        <StyledModal>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Modal id="cancelTicketReasonModal" data-testid="cancel-reason-modal">
                    <Modal.Header
                        id="cancelTicketReasonModalHeader"
                        titleId="ticketList.modal.cancelTicketReason.header"
                        data-testid="cancel-reason-modal-header"
                        onHide={onClose}
                    />
                    <Modal.Body data-testid="cancel-reason-modal-body">
                        <StyledInputContainer>
                            <label
                                id={'cancelTicketReason_label'}
                                data-testid={'cancelTicketReason_label'}
                                htmlFor="cancelTicketReason_textarea"
                            >
                                <FormattedMessage id="ticketList.modal.cancelTicketReason.inputLabel" />
                            </label>
                            <textarea
                                id={'cancelTicketReason_textarea'}
                                className="cancelTicketReason"
                                aria-required={false}
                                data-testid={'cancelTicketReason_textarea'}
                                maxLength={CANCEL_REASON_MAX_LENGTH}
                                disabled={false}
                                cols={CANCEL_REASON_COLS}
                                rows={CANCEL_REASON_ROWS}
                                {...register('comment')}
                            />
                            <StyledCharacterReamining id={'cancelTicketReason_charactersLeft'}>
                                <FormattedMessage
                                    id="ticketList.characterLeft"
                                    values={{
                                        charLeft: CANCEL_REASON_MAX_LENGTH - watch('comment').length,
                                    }}
                                />
                            </StyledCharacterReamining>
                        </StyledInputContainer>
                    </Modal.Body>
                    <Modal.Controls data-testid="cancel-reason-modal-controls">
                        <GIButton
                            id="cancelTicketReasonModalCancelButton"
                            onClickHandler={onClose}
                            variant="secondary"
                            labelId="button.cancel"
                        />
                        <GIButton
                            id="cancelTicketReasonModalSaveButton"
                            variant="primary"
                            labelId="button.submit"
                            type="submit"
                        />
                    </Modal.Controls>
                </Modal>
            </form>
        </StyledModal>
    );
};
